import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
export default function SecureYourSpot() {
  return <section data-section-id="256435" id="secure-your-spot" className="w-full py-16 md:py-24 lg:py-32 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="mb-12 text-center md:text-left">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight mb-4 text-foreground">
            Secure Your Spot
          </h1>
          <p className="text-muted-foreground font-serif text-lg md:text-xl max-w-2xl italic">
            Join your fellow Skyline High alumni for an unforgettable 20-year milestone. Review your registration details and finalize your ticket purchase below.
          </p>
          <div className="w-full h-px bg-gradient-to-r from-accent/50 to-transparent mt-8 hidden md:block"></div>
        </div>

        <div className="w-full bg-secondary/30 backdrop-blur-md text-card-foreground rounded-lg shadow-xl border border-accent/20 overflow-hidden">
          <div className="p-4 md:p-8 lg:p-12">
            <FormProvider formId="secure_spot_form_home" sectionName="Secure Your Spot" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            graduation_year: "2005",
            guest_count: "0",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            graduation_year: {
              required: true
            },
            guest_count: {
              required: true
            }
          }} submitText="Register Now" successMessage="Thank you for registering! We can't wait to see you at the reunion.">
              <RegistrationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function RegistrationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center bg-secondary/50 border border-accent/30 rounded-lg">
        <h3 className="text-2xl font-serif font-bold text-accent mb-4">Registration Received!</h3>
        <p className="text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-accent text-accent hover:bg-accent hover:text-primary">
          Register Another Guest
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-accent uppercase tracking-wider text-xs font-bold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-background/50 border-accent/20 focus:border-accent transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-accent uppercase tracking-wider text-xs font-bold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-background/50 border-accent/20 focus:border-accent transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-accent uppercase tracking-wider text-xs font-bold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" {...field} className="bg-background/50 border-accent/20 focus:border-accent transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="graduation_year" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-accent uppercase tracking-wider text-xs font-bold">Graduation Year</FormLabel>
              <FormControl>
                <Input placeholder="2005" {...field} className="bg-background/50 border-accent/20 focus:border-accent transition-colors" />
              </FormControl>
              <FormDescription className="text-[10px] opacity-70">Confirming your class of 2005 status.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guest_count" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-accent uppercase tracking-wider text-xs font-bold">Number of Guests</FormLabel>
              <FormControl>
                <Input type="number" min="0" max="5" {...field} className="bg-background/50 border-accent/20 focus:border-accent transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="md:col-span-2">
          <FormField control={control} name="message" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-accent uppercase tracking-wider text-xs font-bold">Special Requests or Memories</FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us what you've been up to or any dietary requirements..." {...field} className="bg-background/50 border-accent/20 focus:border-accent transition-colors min-h-[120px]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {submitError && <div className="p-4 mb-6 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="flex justify-center md:justify-start">
        <Button type="submit" disabled={isSubmitting} className="px-12 py-6 text-lg font-bold bg-gradient-to-r from-accent to-accent/80 text-primary hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing...
            </> : 'Secure My Spot'}
        </Button>
      </div>
    </Form>;
}